import { type ReactNode, useEffect } from 'react';
import { createBrowserRouter, Link, Navigate, RouterProvider, useLocation } from 'react-router';
import { DialogHost } from './components/ui/DialogHost';
import { FullPageSpinner } from './components/ui/Spinner';
import { Toaster } from './components/ui/Toaster';
import { TooltipProvider } from './components/ui/Tooltip';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { BoardPage } from './pages/BoardPage';
import { DashboardPage } from './pages/DashboardPage';
import { SpacePage } from './pages/SpacePage';
import { useSession } from './stores/session';

function RequireAccount({ children }: { children: ReactNode }) {
  const user = useSession((s) => s.user);
  const location = useLocation();
  if (!user)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function NotFoundPage() {
  return (
    <div className="dotted-backdrop grid min-h-full place-items-center px-4 text-center">
      <div>
        <p className="text-6xl font-semibold text-slate-300 dark:text-slate-700">404</p>
        <p className="mt-2 text-slate-600 dark:text-slate-300">This page doesn’t exist.</p>
        <Link to="/" className="mt-4 inline-block font-medium text-brand-600 hover:underline">
          Go to your boards
        </Link>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RequireAccount>
        <DashboardPage />
      </RequireAccount>
    ),
  },
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  {
    path: '/s/:spaceId',
    element: (
      <RequireAccount>
        <SpacePage />
      </RequireAccount>
    ),
  },
  { path: '/b/:boardId', element: <BoardPage /> },
  { path: '*', element: <NotFoundPage /> },
]);

export function App() {
  const status = useSession((s) => s.status);
  const load = useSession((s) => s.load);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <TooltipProvider delayDuration={400}>
      {status === 'loading' ? <FullPageSpinner /> : <RouterProvider router={router} />}
      <Toaster />
      <DialogHost />
    </TooltipProvider>
  );
}
